import type { CustomerCompanyMessage } from '../../types/CustomerCompanyMessage';
import type { SupplierCompanyMessage } from '../../types/SupplierCompanyMessage';
import { Button } from '../cdl/Button/Button';
import { useDrawer } from '../cdl/Drawer/useDrawer.hook';
import { Text } from '../cdl/Text/Text';
import { translate } from '../common/helpers/translate.helper';
import { Box } from '../common/ui/Box';

import { GeneralInformationDatasheet } from './GeneralInformationDatasheet';
import { GeneralInformationForm } from './GeneralInformationForm';

interface GeneralInformationProps<T> {
    companyDetails: T;
    onEditSubmit: (data: T) => Promise<void>;
}

export const GeneralInformation = <T extends CustomerCompanyMessage | SupplierCompanyMessage>({
    companyDetails,
    onEditSubmit,
}: GeneralInformationProps<T>) => {
    const { push, pop } = useDrawer();

    const openGeneralInformationSettingsModal = () => {
        const onSubmit = async (values: T) => {
            try {
                await onEditSubmit(values);
                pop(drawerId);
            } catch {
                return;
            }
        };

        const drawerId = push({
            header: { title: translate('company.generalInformation.editCompany') },
            content: <GeneralInformationForm data={companyDetails} onSubmit={onSubmit} />,
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
                <Text variant="subtitle1" as="h2">
                    {translate('company.detailsHeadline')}
                </Text>

                <Button onClick={openGeneralInformationSettingsModal}>{translate('actions.edit')}</Button>
            </Box>

            <GeneralInformationDatasheet companyData={companyDetails} />
        </Box>
    );
};
